import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Stethoscope, HeartPulse, Pill, Building2, ArrowRight } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { openWhatsApp } from "@/lib/whatsapp";

const ROLES = [
  {
    icon: Stethoscope,
    color: "#5B2C91",
    title: "Medici",
    desc: "Sei un medico? Candidati per effettuare visite assistite a distanza, con orari flessibili. Gli orari li decidi tu.",
    cta: "Candidati su WhatsApp",
    waMessage: "Buongiorno, sono un medico e vorrei candidarmi per collaborare con TytoCare.",
    register: { to: "/area-riservata/registrazione-medico", label: "Registrati online" },
  },
  {
    icon: HeartPulse,
    color: "#0EA5E9",
    title: "Infermieri",
    desc: "Sei un infermiere qualificato? Unisciti alla rete territoriale che raggiunge i pazienti a domicilio con il kit TytoCare.",
    cta: "Candidati su WhatsApp",
    waMessage: "Buongiorno, sono un infermiere/un'infermiera e vorrei candidarmi per collaborare con TytoCare.",
    register: { to: "/area-riservata/registrazione-infermiere", label: "Registrati online" },
  },
  {
    icon: Pill,
    color: "#10B981",
    title: "Farmacie",
    desc: "Vuoi offrire visite mediche assistite ai tuoi clienti? Attiva una collaborazione con la tua farmacia.",
    cta: "Collabora con noi",
    waMessage: "Buongiorno, rappresento una farmacia e vorrei attivare una collaborazione con TytoCare.",
  },
  {
    icon: Building2,
    color: "#FF7EB3",
    title: "Altre strutture",
    desc: "RSA, hotel, aziende o altre realtà interessate? Scopri come portare la telemedicina assistita nella tua struttura.",
    cta: "Scrivici su WhatsApp",
    waMessage: "Buongiorno, rappresento una struttura e vorrei ricevere maggiori informazioni per collaborare con TytoCare.",
  },
];

export default function WorkWithUsSection() {
  return (
    <section id="lavora-con-noi" className="bg-white py-20 sm:py-28" data-testid="work-with-us-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-[#F6F4FA] px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#5B2C91]"
          >
            <span className="h-2 w-2 rounded-full bg-[#D4F84A]" />
            Lavora con noi
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#1F152E]"
            data-testid="work-with-us-title"
          >
            Costruiamo insieme la sanità a domicilio
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-5 text-base sm:text-lg leading-relaxed text-[#4A4059]"
          >
            Medici, infermieri, farmacie e strutture: scegli il profilo che ti rappresenta e
            scrivici su WhatsApp. Ti ricontattiamo per valutare insieme la collaborazione.
          </motion.p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ROLES.map((r, i) => {
            const Icon = r.icon;
            return (
              <motion.div
                key={r.title}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: (i % 4) * 0.07, ease: [0.16, 1, 0.3, 1] }}
                className="flex flex-col rounded-3xl bg-white p-6 ring-1 ring-[#5B2C91]/8 shadow-[0_8px_30px_rgb(91,44,145,0.05)] transition-transform duration-300 hover:-translate-y-1.5"
                data-testid={`work-with-us-card-${r.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span
                  className="inline-flex h-12 w-12 items-center justify-center rounded-2xl"
                  style={{ backgroundColor: `${r.color}18`, color: r.color }}
                >
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="font-heading mt-5 text-xl font-semibold text-[#1F152E]">{r.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-[#6B6280]">{r.desc}</p>
                <button
                  onClick={() => openWhatsApp(r.waMessage)}
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#5B2C91]/8 px-5 py-3 text-sm font-semibold text-[#5B2C91] hover:bg-[#5B2C91] hover:text-white transition-colors duration-200"
                  data-testid={`work-with-us-cta-${r.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <FaWhatsapp className="h-4 w-4" />
                  {r.cta}
                </button>
                {r.register && (
                  <Link
                    to={r.register.to}
                    className="mt-3 inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-[#5B2C91] hover:text-[#4A2277] transition-colors"
                    data-testid={`work-with-us-register-${r.title.toLowerCase()}`}
                  >
                    {r.register.label}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
